/**
 * Flow Optimizer — drawer title row: label, flow name, Voluum id, refresh.
 */

import type { FC } from 'react';
import { CircleDot } from 'lucide-react';
import type { FlowOptimizerConfig } from '@trafficpulse/shared';

import {
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/layout/Drawer';
import { cn } from '@/lib/utils';

import { useFlowOptimizerDrawerFooter } from './FlowOptimizerDrawerFooterContext';
import CopyableId from './components/CopyableId';
import { RefreshDrawerButton } from './refresh';
import type { FlowOptimizerProvider } from './services/flow-optimizer-api';

export interface FlowOptimizerDrawerHeaderProps {
  flowId: string;
  campaignId: string;
  /** Optional display names — shown when the call site has them. */
  campaignName?: string;
  flowName?: string;
  provider: FlowOptimizerProvider;
  /** The live draft — the refresh tooltip prices its date ranges. */
  config: FlowOptimizerConfig;
}

const FlowOptimizerDrawerHeader: FC<FlowOptimizerDrawerHeaderProps> = ({
  flowId,
  campaignId,
  campaignName,
  flowName,
  provider,
  config,
}) => {
  const { headerSlot } = useFlowOptimizerDrawerFooter();
  // Names fall back to the raw id when the provider lookup fails; in that case
  // defer (flow → preview-supplied name; campaign → hide, the id is shown by the
  // copy button beside it) so we never print the id twice.
  const flowLabel =
    (flowName && flowName !== flowId ? flowName : '') || headerSlot?.flowName || '';
  const campaignLabel =
    campaignName && campaignName !== campaignId ? campaignName : '';

  return (
    <DrawerHeader className="gap-0 px-4 pb-1.5 pt-2.5">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
          <DrawerTitle className="shrink-0 text-sm font-semibold leading-tight">
            Flow Optimizer
          </DrawerTitle>
          <span
            className="mx-0.5 h-3 w-px shrink-0 self-center bg-border/90"
            aria-hidden
          />
          {flowLabel ? (
            <span className="min-w-0 max-w-[min(20rem,40vw)] shrink truncate text-xs font-semibold leading-tight text-foreground">
              {flowLabel}
            </span>
          ) : null}
          <CopyableId value={flowId} label="Flow ID" />
          <span
            className="mx-0.5 h-3 w-px shrink-0 self-center bg-border/90"
            aria-hidden
          />
          <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
            Campaign
          </span>
          {campaignLabel ? (
            <span className="min-w-0 max-w-[min(16rem,32vw)] shrink truncate text-xs font-medium leading-tight text-foreground">
              {campaignLabel}
            </span>
          ) : null}
          <CopyableId value={campaignId} label="Campaign ID" />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {headerSlot ? (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 rounded-full border border-warning/40 bg-warning/10 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-warning',
                !headerSlot.dirty && 'invisible',
              )}
              title={headerSlot.dirty ? 'You have unsaved changes' : undefined}
              aria-hidden={!headerSlot.dirty}
            >
              <CircleDot className="h-2.5 w-2.5 shrink-0" aria-hidden />
              Unsaved
            </span>
          ) : null}
          <RefreshDrawerButton
            campaignId={campaignId}
            provider={provider}
            config={config}
          />
        </div>
      </div>
      <DrawerDescription className="sr-only">
        Configure path, lander, and offer weights. Calculations run on the
        backend.
      </DrawerDescription>
    </DrawerHeader>
  );
};

export default FlowOptimizerDrawerHeader;
